// eslint-disable-next-line @typescript-eslint/no-var-requires
const { STATIC_ATTR, titleFor } = require('./socialTags');

/**
 * Removes the head tags scripts/prerender-meta.js wrote for crawlers, and puts the
 * static <title> back to the homepage's. Call before React renders. (#814)
 *
 * Those tags exist for Instagram, Facebook, Reddit, LinkedIn and Slack, which read the
 * served HTML and never run JavaScript, so none of them ever reaches this line. Once
 * JavaScript is running, React owns the head: on React 19, helmet-async renders every
 * page's tags as React elements and React hoists them into <head>. React does not
 * adopt tags that were already there. Left in place, the static copies sit first in
 * the document beside React's, and on the first client-side navigation they go stale:
 * a visitor who lands on the assistant page and clicks through to /compare would carry
 * the assistant page's description and canonical ahead of the right ones.
 *
 * The <title> is reset rather than removed. React inserts its own title in front of
 * the static one, so on a page that sets a title the static one is inert. On a page
 * that sets none (/register, and every page inside the app) React removes its title
 * and the static one becomes the tab title again. Before #814 that static title was
 * always the homepage's, which is harmless. Now the build writes the landing page's
 * title into it, so without this reset, someone who arrives on /compare from an ad and
 * signs up would carry "Chaptery vs. Scrivener..." in their tab, history and bookmarks
 * for the rest of the session. Resetting restores exactly the old fallback. Removing
 * it instead would leave those pages with no title at all.
 *
 * Returns how many tags were removed, which only the tests use.
 */
export function removeStaticHead(doc: Document = document): number {
  if (!doc.head) return 0;
  const nodes = Array.from(doc.head.querySelectorAll(`[${STATIC_ATTR}]`));
  nodes.forEach((n) => n.parentNode?.removeChild(n));

  // The LAST title, not the first. React inserts its own titles in front of the
  // static one, so the static one is always last. Taking the first would, after React
  // had rendered, overwrite React's title and leave the stale one in place.
  const titles = doc.head.querySelectorAll('title');
  const staticTitle = titles[titles.length - 1];
  if (staticTitle) staticTitle.textContent = titleFor('/');

  return nodes.length;
}
